import React from 'react';
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Medal } from "lucide-react";

interface AccreditationLevel {
  name: string;
  hours: number;
  color: string;
}

const ICF_LEVELS: AccreditationLevel[] = [
  { name: 'ACC', hours: 100, color: '#9b87f5' },
  { name: 'PCC', hours: 500, color: '#7E69AB' },
  { name: 'MCC', hours: 2500, color: '#6E59A5' },
];

const EMCC_LEVELS: AccreditationLevel[] = [
  { name: 'Foundation', hours: 0, color: '#33C3F0' },
  { name: 'Practitioner', hours: 100, color: '#1EAEDB' },
  { name: 'Senior Practitioner', hours: 500, color: '#0EA5E9' },
  { name: 'Master Practitioner', hours: 2500, color: '#0FA0CE' },
];

interface AccreditationProgressProps {
  totalHours: number;
}

export const AccreditationProgress: React.FC<AccreditationProgressProps> = ({ totalHours }) => {
  const getCurrentLevel = (levels: AccreditationLevel[]) => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (totalHours >= levels[i].hours) {
        return levels[i];
      }
    }
    return levels[0];
  };

  const getNextLevel = (levels: AccreditationLevel[]) => {
    for (let i = 0; i < levels.length; i++) {
      if (totalHours < levels[i].hours) {
        return levels[i];
      }
    }
    return null;
  };

  const calculateProgress = (current: number, target: number) => {
    return Math.min(Math.round((current / target) * 100), 100);
  };

  const icfCurrentLevel = getCurrentLevel(ICF_LEVELS);
  const icfNextLevel = getNextLevel(ICF_LEVELS);
  const emccCurrentLevel = getCurrentLevel(EMCC_LEVELS);
  const emccNextLevel = getNextLevel(EMCC_LEVELS);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Accreditation Progress</CardTitle>
        <CardDescription>Track your progress towards ICF and EMCC certifications</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">ICF Progress</h3>
            <div className="flex items-center gap-2">
              <Medal className="h-4 w-4 text-coaching-primary" />
              <span className="text-sm font-medium">{totalHours} hours logged</span>
            </div>
          </div>
          <div className="space-y-2">
            {icfNextLevel ? (
              <>
                <div className="flex justify-between text-sm">
                  <span>{icfCurrentLevel?.name || 'Start'}</span>
                  <span>{icfNextLevel.name}</span>
                </div>
                <Progress 
                  value={calculateProgress(totalHours, icfNextLevel.hours)} 
                  className="h-2"
                />
                <p className="text-sm text-muted-foreground">
                  {icfNextLevel.hours - totalHours} hours needed for {icfNextLevel.name}
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 text-coaching-primary">
                <CheckCircle2 className="h-5 w-5" />
                <span>Achieved MCC level!</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">EMCC Progress</h3>
            <div className="flex items-center gap-2">
              <Medal className="h-4 w-4 text-coaching-primary" />
              <span className="text-sm font-medium">{totalHours} hours logged</span>
            </div>
          </div>
          <div className="space-y-2">
            {emccNextLevel ? (
              <>
                <div className="flex justify-between text-sm">
                  <span>{emccCurrentLevel.name}</span>
                  <span>{emccNextLevel.name}</span>
                </div>
                <Progress 
                  value={calculateProgress(totalHours, emccNextLevel.hours)} 
                  className="h-2"
                />
                <p className="text-sm text-muted-foreground">
                  {emccNextLevel.hours - totalHours} hours needed for {emccNextLevel.name}
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 text-coaching-primary">
                <CheckCircle2 className="h-5 w-5" />
                <span>Achieved Master Practitioner level!</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
